// libraries
import { Grid } from '@interstate/components/Grid';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import type { DealHistory } from '@makemydeal/dr-dash-types';

// consts
import * as constants from '../../constants';

import { taxElementsToLineItems } from '../utils/transform';

// hooks
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// components
import { DealSummaryItemWrapper } from '../common/DealSummaryItemWrapper';
import LineItemWithChildren from '../common/LineItemWithChildren';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    expandibleAreaStyles,
    expandibleButtonStyles,
    labelStyles as defaultLabelStyles,
    secondaryValueStyles,
    valueStyles as defaultValueStyles,
    detailedViewLabelStyles,
    detailedViewValueStyles,
    detailedViewGridProps,
    defaultGridProps
} from '../utils/styles';
import DealSummaryItem from '../common/DealSummaryItem';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type TotalTaxProps = {
    deal?: DealHistory;
    useWrapper?: boolean;
    hasDelta?: boolean;
};

const TotalTax = ({ deal, useWrapper = true, hasDelta = false }: TotalTaxProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'TotalTax');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const totalTax = useHeldValue(useDealRootSelector(offerReduxSelectors.getTotalTax, 'totalTax'), deal);

    const additionalBreakdown = useDealRootSelector(offerSelectors.getAdditionalTaxBreakdown, 'additionalTaxBreakdown');
    const additionalItems = taxElementsToLineItems(additionalBreakdown);
    const additionalTotal = useDealRootSelector(offerSelectors.getAdditionalTaxTotal, 'additionalTaxTotal');

    const primaryBreakdown = useDealRootSelector(offerReduxSelectors.getPrimaryTaxBreakdown, 'primaryTaxBreakdown');
    const primaryTotal = useDealRootSelector(offerSelectors.getPrimaryTaxTotal, 'primaryTaxTotal');
    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'TotalTax',
        field: 'enhancedDealDetails'
    });

    if (primaryBreakdown == null) {
        return null;
    }

    const primaryItems = taxElementsToLineItems(primaryBreakdown);

    const isPrimary = primaryTotal > 0 && primaryItems !== null;
    const isAdditional = additionalTotal > 0 && additionalItems !== null;

    const labelStyles = showDetailedView ? detailedViewLabelStyles : defaultLabelStyles;
    const valueStyles = showDetailedView ? detailedViewValueStyles : defaultValueStyles;
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    return (
        <div>
            {isEnhancedDealDetailsEnabled ? (
                <DealSummaryItem
                    valueStyles={valueStyles}
                    labelStyles={!showDetailedView ? labelStyles : {}}
                    label={constants.TOTAL_TAXES}
                    value={formatDollarsAndCents(totalTax)}
                    hasDelta={hasDelta}
                >
                    <Grid sx={gridProps}>
                        {isPrimary && (
                            <DealSummaryItem
                                valueStyles={secondaryValueStyles}
                                labelStyles={labelStyles}
                                expandibleButtonStyles={expandibleButtonStyles}
                                expandibleAreaStyles={expandibleAreaStyles}
                                label={constants.PRIMARY_TAXES}
                                value={formatDollarsAndCents(primaryTotal)}
                            >
                                {primaryItems.map(({ label: childLabel, value: childValue }) => (
                                    <LineItemWithChildren
                                        containerStyles={{
                                            pr: 0
                                        }}
                                        label={childLabel}
                                        value={childValue}
                                    />
                                ))}
                            </DealSummaryItem>
                        )}
                        {isAdditional && (
                            <DealSummaryItem
                                valueStyles={secondaryValueStyles}
                                labelStyles={labelStyles}
                                expandibleButtonStyles={expandibleButtonStyles}
                                expandibleAreaStyles={expandibleAreaStyles}
                                label={constants.ADDL_TAXES}
                                value={formatDollarsAndCents(additionalTotal)}
                            >
                                {additionalItems.map(({ label: childLabel, value: childValue }) => (
                                    <LineItemWithChildren
                                        containerStyles={{
                                            pr: 0
                                        }}
                                        label={childLabel}
                                        value={childValue}
                                    />
                                ))}
                            </DealSummaryItem>
                        )}
                    </Grid>
                </DealSummaryItem>
            ) : (
                <DealSummaryItemWrapper
                    label={constants.TOTAL_TAXES}
                    value={formatDollarsAndCents(totalTax)}
                    useWrapper={useWrapper}
                    hasDelta={hasDelta}
                >
                    {(isPrimary || isAdditional) && (
                        <>
                            {isPrimary && (
                                <LineItemWithChildren
                                    testId="deal-summary-lineitem-capitalized-taxes"
                                    label={constants.PRIMARY_TAXES}
                                    value={formatDollarsAndCents(primaryTotal)}
                                    childItems={primaryItems}
                                />
                            )}
                            {isAdditional && (
                                <LineItemWithChildren
                                    label={constants.ADDL_TAXES}
                                    value={formatDollarsAndCents(additionalTotal)}
                                    childItems={additionalItems}
                                />
                            )}
                        </>
                    )}
                </DealSummaryItemWrapper>
            )}
        </div>
    );
};

export default TotalTax;
